<template>
  <Box>
    <BoxHead icon="fas fa-water">
      Assets & Liabilities
    </BoxHead>

    <BoxLoader v-if="loading" />

    <EmptySection
      v-else-if="isEmpty"
      :badges="['Assets', 'Liabilities']"
      :entity-external-id="entityExternalId"
    />

    <div
      v-else
      class="table-with-chart"
    >
      <Stack
        gap="4"
        class="table-main"
      >
        <DetailsTitle>
          Assets
        </DetailsTitle>

        <DataTable
          :head="assetsTable.head"
          :body="assetsTable.body"
          :foot="assetsTable.foot"
        />

        <DetailsTitle>
          Liabilities
        </DetailsTitle>

        <DataTable
          :head="liabilitiesTable.head"
          :body="liabilitiesTable.body"
          :foot="liabilitiesTable.foot"
        />

        <DetailsTitle
          icon="fas fa-coins"
          :value="formatCurrency(netWorth)"
        >
          <FormulaTooltip
            v-if="netWorthFormula"
            :formula="netWorthFormula"
            :entity-external-id="entityExternalId"
          >
            Net Worth
          </FormulaTooltip>
          <span v-else>
            Net Worth
          </span>
        </DetailsTitle>
      </Stack>

      <div
        v-if="assetClassBreakdown && assetClassBreakdown.length > 0"
        class="table-chart"
      >
        <HighChartsWrapper
          name="asset-class-breakdown"
          :chart-options="assetClassHighchartOptions"
          chart-type="pie"
        />
      </div>
    </div>
  </Box>
</template>

<script setup>
import { computed } from 'vue';
import { Box, BoxHead, BoxLoader, Stack, DataTable, DetailsTitle } from '@stakeui';
import { formatCurrency } from '@frontend/shared/Util.js';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import EmptySection from './EmptySection.vue';
import { isEmptyValue } from '@frontend/shared/Util.js';
import FormulaTooltip from '@frontend/ui/components/FormulaTooltip.vue';

const props = defineProps({
  client: {
    type: Object,
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  entityExternalId: {
    type: String,
    required: true,
  },
});

const assetClassBreakdown = computed(() => props.client?.asset_class_breakdown);
const assets = computed(() => props.client?.assets);
const totalAssets = computed(() => props.client?.total_assets);
const liabilities = computed(() => props.client?.liabilities);
const totalLiabilities = computed(() => props.client?.total_liabilities);
const netWorth = computed(() => props.client?.net_worth);

const formulas = computed(() => props.client?._formulas || {});
const netWorthFormula = computed(() => formulas.value.net_worth);

const isEmpty = computed(() => {
  return isEmptyValue(assetClassBreakdown.value) &&
         isEmptyValue(assets.value) &&
         isEmptyValue(totalAssets.value) &&
         isEmptyValue(liabilities.value) &&
         isEmptyValue(totalLiabilities.value) &&
         isEmptyValue(netWorth.value);
});

const assetsTable = computed(() => {
  if (!assets?.value) {
    return {};
  }

  const body = assets.value.map((asset, index) => ({
    id: index,
    cells: [
      { id: 'type', label: asset.type },
      { id: 'description', label: asset.description },
      { id: 'amount', label: formatCurrency(asset.value), type: 'numeric' },
    ],
  }
  ));

  body.push({
    id: 'total',
    variant: 'bold',
    cells: [
      { id: 'type', label: 'Total Assets', colSpan: 2 },
      { id: 'amount', label: formatCurrency(totalAssets.value), type: 'numeric' },
    ],
  });

  return {
    head: [
      { id: 'type', label: 'Type', width: '12em' },
      { id: 'description', label: 'Description' },
      { id: 'amount', label: 'Amount', type: 'numeric', width: '8em' },
    ],
    body,
  };
});

const liabilitiesTable = computed(() => {
  if (!liabilities?.value) {
    return {};
  }

  const body = liabilities.value.map((liability, index) => ({
    id: index,
    cells: [
      { id: 'type', label: liability.type },
      { id: 'description', label: liability.description },
      { id: 'amount', label: formatCurrency(liability.balance), type: 'numeric' },
    ],
  }
  ));

  body.push({
    id: 'total',
    variant: 'bold',
    cells: [
      { id: 'type', label: 'Total Liabilities', colSpan: 2 },
      { id: 'amount', label: formatCurrency(totalLiabilities.value), type: 'numeric' },
    ],
  });

  return {
    head: [
      { id: 'type', label: 'Type', width: '12em' },
      { id: 'description', label: 'Description' },
      { id: 'amount', label: 'Amount', type: 'numeric', width: '8em' },
    ],
    body,
  };
});

const assetClassHighchartOptions = computed(() => {
  if (!props.client?.asset_class_breakdown?.length) {
    return {
      chart: {
        type: 'pie',
        height: 200,
      },
      series: [{
        data: [],
      }],
    };
  }

  const colors = [
    '#4D96FF', '#6BCB77', '#FF6B6B', '#FFD93D', '#9856F0',
    '#4E9F3D', '#E86A33', '#8294C4', '#ACB1D6', '#DBDFEA',
  ];

  const data = props.client.asset_class_breakdown.map((item, index) => ({
    name: item.asset_class,
    y: item.value,
    color: item.color || colors[index % colors.length],
  }));

  return {
    chart: {
      type: 'pie',
      height: 200,
    },
    title: {
      text: null,
    },
    tooltip: {
      pointFormat: '{point.name}: <b>{point.percentage:.1f}%</b><br/>Value: <b>{point.y:,.0f}</b>',
    },
    plotOptions: {
      pie: {
        allowPointSelect: true,
        cursor: 'pointer',
        dataLabels: {
          enabled: true,
          format: '<b>{point.name}</b>: {point.percentage:.1f} %',
          distance: 20,
        },
        showInLegend: true,
      },
    },
    series: [{
      name: 'Asset Classes',
      colorByPoint: true,
      data: data,
    }],
  };
});
</script>

<style scoped>
.table-with-chart {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.table-main {
  flex-grow: 1;
}

.table-chart {
  flex-shrink: 0;
  width: 20rem;
}
</style>
